import {Component, DestroyRef, OnDestroy, OnInit} from '@angular/core';
import {MetaData, NgEventBus} from 'ng-event-bus';
import {LookupService} from '../../common/service/lookup.service';
import {PlanModelService} from '../../common/service/plan-model.service';
import {PermissionsService} from '../../common/service/permissions.service';
import {TraitsService} from '../../common/service/traits.service';
import {Subscription} from 'rxjs';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {NgClass} from '@angular/common';
import {AcquisitionTransferAction} from '../../common/models/acquisition-transfer-action.model';
import {NlrFormFieldHasErrorPipe} from '../../common/pipe/nlr-form-field-has-error.pipe';
import {SpatialUtilService} from '../../design/services/spatial-util.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PDF_CREATION_METHOD} from '../../common/constants';

/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({
    selector: 'survey-comments',
    imports: [FormsModule, NgClass, NlrFormFieldHasErrorPipe],
    templateUrl: './survey-comments.component.html',
    styleUrl: './survey-comments.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class SurveyCommentsComponent implements OnInit, OnDestroy {
    title = 'Notations and statements';

    surveyAndPlanNotificationTitle = 'Survey and plan notation';

    acquisitionActionStatementTitle = 'Acquisition action statement';

    transferActionStatementTitle = 'Transfer action statement';

    permissions = ['EDIT_CERTIFICATION', 'VIEW_CERTIFICATION'];

    traitsChangedSubscription: Subscription;

    acquisitionTransferActionLookups: AcquisitionTransferAction[] = [];

    showStatements: boolean = true;

    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private permissionsService: PermissionsService,
        private traitsService: TraitsService,
        private spatialUtilService: SpatialUtilService,
        private destroyRef: DestroyRef
    ) {}

    ngOnInit() {
        this.acquisitionTransferActionLookups = this.lookupService.getLookups('acquisitionTransferAction');

        this.traitsChangedSubscription = this.eventBus
            .on('nlr:trait-service:traitsChanged')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((meta: MetaData) => {
                console.log(`${this.title} - ngOnInit - nlr:trait-service:traitsChanged`, meta.data);

                this.showStatements = this.acquisitionTransferActionLookups.some(acquisitionTransferAction =>
                    this.traitsService.criteriaMatches(acquisitionTransferAction.planTraitCriteria, false)
                );
            });
    }

    ngOnDestroy() {
        this.traitsChangedSubscription.unsubscribe();
    }

    showComponent() {
        return this.permissionsService.hasPermission(this.permissions);
    }

    getAcquisitionActionStatement() {
        return this.actionStatement(
            'Acquired for a public purpose',
            this.getDigitalPlanAttachment()?.lotsAcquiredForPublicPurpose,
            'TO BE ACQUIRED FOR A PUBLIC PURPOSE.'
        );
    }

    getTransferActionStatement() {
        return this.actionStatement('Transferred to the Crown', this.getDigitalPlanAttachment()?.lotsTransferredToCrown, 'TO BE TRANSFERRED TO THE CROWN.');
    }

    /**
     * A portal plan holds the acquisition transfer action against the lot itself, so the statement is built from the lots it
     * holds; a digital plan has no lots to hold it against, so the lot numbers declared on the Digital Plan tab are used
     * instead. The declaration is its own evidence that the plan can carry the action, so it is not gated on the plan traits
     * that decide whether a portal surveyor may pick one.
     */
    private actionStatement(action: string, declaredLots: number[], suffix: string): string {
        const digitalPlan = this.planModelService.getModel().pdfCreationMethod === PDF_CREATION_METHOD.DIGITAL_PLAN;
        if (!digitalPlan && !this.showStatements) {
            return '';
        }

        const actionIds = this.acquisitionTransferActionLookups.filter(l => l.description === action).map(l => l.id);
        const lotNumbers = digitalPlan
            ? [...(declaredLots ?? [])].sort((a, b) => a - b)
            : (this.spatialUtilService.getLots() ?? [])
                  .filter(lot => actionIds.includes(Number(lot.attributes.acquisitiontransferaction)))
                  .map(lot => Number(lot.attributes.name));

        if (!lotNumbers.length) {
            return '';
        }

        const groupedLotNumbers = this.spatialUtilService.groupContinuousNumbers(lotNumbers);
        const joinedGroupedLotNumbers = groupedLotNumbers.join(', ');
        if (groupedLotNumbers.length === 1) {
            return `LOT${lotNumbers.length > 1 ? 'S' : ''} ${joinedGroupedLotNumbers} ${suffix}`;
        }

        // get the last index of ',' and replace it with 'AND'
        return `LOTS ${this.replaceAt(joinedGroupedLotNumbers, joinedGroupedLotNumbers.lastIndexOf(', '), ' AND')} ${suffix}`;
    }

    private getDigitalPlanAttachment() {
        return this.planModelService.getModel().digitalPlanAttachment;
    }

    getCertification() {
        return this.planModelService.getModel().certification;
    }

    hasEditPermission() {
        return !this.planModelService.readonly && this.permissionsService.hasEditPermission(this.permissions);
    }

    private replaceAt(value: string, index: number, replacement: string) {
        return value.substring(0, index) + replacement + value.substring(index + 1);
    }
}
